import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject, Observable, filter, map, of, takeUntil, withLatestFrom } from 'rxjs';
import { NgxsFormPluginModule, UpdateFormValue } from '@ngxs/form-plugin';
import { Select, ofActionSuccessful } from '@ngxs/store';
import { TranslateModule } from '@ngx-translate/core';

import { DropDownItem, WebApiResponse, WebApiResponseBase } from '@app/shared/classes/general';
import { NgxsActionName, NgxsFormPath } from '@app/shared/constants';
import { AngularMaterialModule } from '@app/shared/modules/angular-material.module';
import { AppDataState } from '@app/shared/stores/app-data.state';
import { EditModalDialogBasedComponent } from '@shared/components/edit-modal-dialog/edit-modal-dialog.based.component';
import { SpinnerButtonComponent } from '@shared/components/spinner-button/spinner-button.component';
import * as AppDataAction from '@app/shared/actions/app-data.action';
import { UserProfile } from '@app/shared/classes/user-profile';
import { InjectDialogData } from '@shared/components/edit-modal-dialog/edit-modal-dialog.constant';
import { UserProfileSearchGridComponent } from '@shared/components/user-profile-search-grid/user-profile-search-grid.component';
import { UserProfileRole } from '@app/shared/classes/user-profile-role';
import { SiteConfigHelper } from '@app/shared/utils/site-config.helper';
import { SubRegion } from '@app/shared/classes/subRegion';
import { Terminal } from '@app/shared/classes/terminal';
import { Utilities } from '@app/shared/utils/utilities';
import { BusinessHelper } from '@app/shared/utils/business-helper';
import { DriverDataBusinessType } from '@app/shared/enums';

@Component({
  selector: 'app-user-profile-edit-modal-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    TranslateModule,
    AngularMaterialModule,
    NgxsFormPluginModule,
    ReactiveFormsModule,
    MatDialogModule,
    SpinnerButtonComponent,
    CommonModule,
    UserProfileSearchGridComponent,
  ],
  templateUrl: './user-profile-edit-modal-dialog.component.html',
  styleUrl: './user-profile-edit-modal-dialog.component.css'
})
export class UserProfileEditModalDialogComponent extends EditModalDialogBasedComponent<UserProfileEditModalDialogComponent, UserProfile> {
  formPath = NgxsFormPath.USER_PROFILE_EDIT_FORM;
  title = "";
  driverNameFilterSub = new BehaviorSubject<string>('');
  driverNameFilter$ = this.driverNameFilterSub.asObservable();
  userProfileRoleSelectItems$: Observable<UserProfileRole[]>;
  userNameFilterSub = new BehaviorSubject<string>('');
  userNameFilter$ = this.userNameFilterSub.asObservable();
  countrySelectItems = SiteConfigHelper.GetCountryCodeDropDownList();
  businessTypeSelectItems = BusinessHelper.GetCombinedBusinessTypeDropDownList();

  subRegionSelectItems$: Observable<DropDownItem[]>;
  terminalSelectItems$: Observable<DropDownItem[]>;

  @Select(AppDataState.taskExecuting(NgxsActionName.UPDATE_USER_PROFILE)) processing$: Observable<boolean>;
  @Select(AppDataState.taskError(NgxsActionName.UPDATE_USER_PROFILE)) errorResponse$: Observable<WebApiResponseBase>;
  @Select(AppDataState.taskResponse(NgxsActionName.GET_USER_PROFILE_ROLE)) userProfileRoleResponse$: Observable<WebApiResponse<UserProfileRole[]>>;
  @Select(AppDataState.taskResponse(NgxsActionName.GET_SUB_REGIONS)) subRegions$: Observable<WebApiResponse<SubRegion[]>>;
  @Select(AppDataState.taskResponse(NgxsActionName.GET_SUB_REGION_RELATED_TERMINAL_LIST)) terminals$: Observable<WebApiResponse<Terminal[]>>;

  constructor(public dialogRef: MatDialogRef<UserProfileEditModalDialogComponent>, @Inject(MAT_DIALOG_DATA) public data: InjectDialogData<UserProfile>) {
    super();
    this.dataInit(dialogRef, data);
    this.isCreate = data.isCreate;
    this.initForm(data);
    this.title = data.isCreate ? "ml_144036" : "ml_143925";
  }

  initForm = (data: InjectDialogData<UserProfile>) => {
    if (data.isCreate) {
      this.editForm = this.formBuilder.group({
        userProfile_Id: -1,
        apexId: ["", Validators.required],
        userName: ["", Validators.required],
        country: ["", Validators.required],
        subRegion_Id: [null],
        terminal_Ids: [[]],
        role_id: [null, Validators.required],
        role: null,
        businessTypes: ["", Validators.required]
      });
    }
    else {
      var currentSubRegion = data.data.subRegion_ids ? data.data.subRegion_ids[0] : null;
      this.editForm = this.formBuilder.group({
        userProfile_Id: data.data.userProfile_Id,
        apexId: [data.data.apexId, Validators.required],
        userName: [data.data.fullName, Validators.required],
        country: [data.data.country, Validators.required],
        subRegion_Id: [currentSubRegion],
        terminal_Ids: [data.data.terminal_ids],
        role_id: [data.data.role_id, Validators.required],
        role: data.data.role,
        businessTypes: [BusinessHelper.GetCombinedBusinessType(data.data.businessTypes as DriverDataBusinessType[]), Validators.required]
      });

      this.countrySelectionChanged({ value: data.data.country }, currentSubRegion);
      this.subRegionSelectionChanged({ value: currentSubRegion }, data.data.terminal_ids);
    }
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.actions$.pipe(ofActionSuccessful(AppDataAction.UpdateUserProfile), withLatestFrom(this.errorResponse$), takeUntil(this.destroy$)).subscribe(
      ([_, errorResponse]) => this.processAfterUpdateObject(errorResponse));

    this.userProfileRoleSelectItems$ = this.userProfileRoleResponse$.pipe(filter(Boolean), map(res => res.data), filter(Boolean));
  }

  // For some drop down control issues, we had to put the pipe to be runned here
  countrySelectionChanged = (event: any, selectedSubRegion: number = null) => {
    var selectedCountry = event.value;
    this.store.dispatch(new UpdateFormValue({
      value: { subRegion_Id: selectedSubRegion, terminal_Ids: [] },
      path: this.formPath
    }));

    this.subRegionSelectItems$ = this.subRegions$.pipe(map(subRegions => {
      if (Utilities.isEmptyString(selectedCountry)) {
        return [];
      }

      var countryRelatedSubRegions = subRegions.data.filter(item => item.country === selectedCountry);
      return Utilities.PatchEmptyDropDownItem(countryRelatedSubRegions.map(item => {
        return { value: item.subRegion_Id, label: item.subRegionName } as DropDownItem
      }));
    }));

    this.terminalSelectItems$ = of([]);
  }

  subRegionSelectionChanged = (event: any, selectedTerminals: number[] = []) => {
    this.resetSelectedTerminals(selectedTerminals);
    this.resetTerminalSelectItems(event.value, this.editForm.controls['businessTypes'].value);
  }

  businessTypeSelectionChanged = (event: any, selectedTerminals: number[] = []) => {
    this.resetSelectedTerminals(selectedTerminals);
    this.resetTerminalSelectItems(this.editForm.controls['subRegion_Id'].value, event.value);
  }

  resetSelectedTerminals = (selectedTerminals: number[] = []) => {
    this.store.dispatch(new UpdateFormValue({
      value: { terminal_Ids: selectedTerminals },
      path: this.formPath
    }));
  }

  resetTerminalSelectItems = (selectedSubRegion: number, selectedBusinessType: DriverDataBusinessType) => {
    var userSelectedBusinessTypes = BusinessHelper.GetSeperatedBusinessTypes(selectedBusinessType as DriverDataBusinessType) as string[];
    this.terminalSelectItems$ = this.terminals$.pipe(filter(Boolean), map(terminals => terminals.data), filter(Boolean),
      map(terminals => {
        if (!selectedSubRegion || userSelectedBusinessTypes.length === 0) {
          return [];
        }

        var subregionRelatedTerminals = terminals.filter(item => item.subRegion?.subRegion_Id === selectedSubRegion && userSelectedBusinessTypes.includes(item.businessType));
        return subregionRelatedTerminals.map(item => {
          return { value: item.terminal_Id, label: `${item.terminalId} ${item.terminalName}` } as DropDownItem;
        });
      }));
  }

  onUserNameKeyUp = (event: any) => {
    this.userNameFilterSub.next(event.target.value as string);
  }

  onUserItemClicked = (event: any) => {
    var currentValue = { apexId: '', userName: '' };
    if (event.toggled) {
      var userProfile = event.item as UserProfile;
      currentValue.apexId = userProfile.apexId;
      currentValue.userName = userProfile.fullName;
      this.userNameFilterSub.next("");
    }

    this.store.dispatch(new UpdateFormValue({
      value: currentValue,
      path: this.formPath
    }));
  }

  roleSelectionChanged = (event: any) => {
    var currentRoleId = event.value as number;
    var currentRoleName = event.source.triggerValue;
    let currentSelectedRole: UserProfileRole = { role_Id: currentRoleId, roleName: currentRoleName };
    var currentValue = { role: currentSelectedRole, };
    this.store.dispatch(new UpdateFormValue({
      value: currentValue,
      path: this.formPath
    }));
  }

  onOkClick = () => {
    this.store.dispatch(new AppDataAction.UpdateUserProfile());
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.driverNameFilterSub.complete();
    this.userNameFilterSub.complete();
  }
}
